import 'reflect-metadata';

import { rtDebugMeasure } from './rt-debug-measure';
import {
  isComponentOrDirective,
  recognizeAngularType,
  RtDebugAngularType,
} from './rt-debug-recognizer';
import { RtLogger } from './rt-logger';

export interface RtDebugOptions {
  namespace?: string;
  log?: {
    lifecycle?: boolean | string[];
    warnIfNotSingleton?: boolean;
  };
  measureMethods?: string[];
  enabled?: boolean;
}

export class RtDebugService {
  public logger: typeof RtLogger = RtLogger;
  public measure: typeof rtDebugMeasure = rtDebugMeasure;
  public activeInstances: Map<string, any> = new Map();

  constructor() {
    RtLogger.headline('RtDebug is active');
  }

  public addInstance(ns: string, instance: any): void {
    if (this.activeInstances.has(ns)) {
      this.activeInstances.set(ns, [...this.activeInstances.get(ns), instance]);
    } else {
      this.activeInstances.set(ns, [instance]);
    }

    Object.defineProperty(this, ns, {
      get: () => this.getInstance(ns),
    });
  }

  public removeInstance(ns: string, instance: any): void {
    const instances = (this.activeInstances.get(ns) || []).filter((i: any) => i !== instance);
    if (instances.length === 0) {
      this.activeInstances.delete(ns);
    } else {
      this.activeInstances.set(ns, instances);
    }
  }

  public getInstance(ns: string): any {
    const instances = this.activeInstances.get(ns);
    if (instances.length > 0) {
      if (instances.length > 1) {
        return instances;
      } else {
        return instances[0];
      }
    } else {
      return null;
    }
  }

  public makeClassDebuggable(originalClass: new () => any, options: RtDebugOptions): any {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const ngType = recognizeAngularType(originalClass);

    const DebuggableClass = this.debugableClassFactory(originalClass, options);

    if (options.log && options.log.lifecycle && isComponentOrDirective(ngType)) {
      self.attachLifecycleLogger(
        DebuggableClass,
        originalClass.name,
        ngType,
        options.log.lifecycle,
      );
    }

    if (options.measureMethods) {
      self.attachMeasureMethods(DebuggableClass, originalClass.name, options.measureMethods);
    }

    this.cloneMetadataAnnotations(originalClass, DebuggableClass);

    return DebuggableClass;
  }

  private debugableClassFactory(
    originalClass: new (...args: any[]) => any,
    options: RtDebugOptions,
  ): new () => any {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const debuggableClass = class Debugable extends originalClass {
      constructor(...args: any[]) {
        super(...args);
        self.addInstance(options.namespace!, this);

        if (options.log!.warnIfNotSingleton) {
          const count = self.activeInstances.get(options.namespace!).length;

          if (count > 1) {
            RtLogger.warn(
              `Class ${originalClass.name} is marked to be an singleton, but active instances count is: ${count}`,
            );
          }
        }
      }

      public ngOnDestroy(): void {
        self.removeInstance(options.namespace!, this);

        if (super.ngOnDestroy) {
          super.ngOnDestroy();
        }
      }
    };

    debuggableClass.prototype.name! = `${originalClass.name}Debug`;
    return debuggableClass;
  }

  private attachLifecycleLogger(
    cls: any,
    displayName: string,
    ngType: RtDebugAngularType,
    options: any,
  ): any {
    let lifecycleHooks = [];

    if (!Array.isArray(options)) {
      const componentLifecycleMethodNames = [
        'ngAfterContentInit',
        'ngAfterContentChecked',
        'ngAfterViewInit',
        'ngAfterViewChecked',
        'rtStateWillInit',
        'rtStateWillChange',
      ];
      const directiveLifecycleMethodNames = ['ngOnChanges', 'ngOnInit', 'ngDoCheck', 'ngOnDestroy'];
      lifecycleHooks =
        ngType === RtDebugAngularType.Component
          ? [...componentLifecycleMethodNames, ...directiveLifecycleMethodNames]
          : directiveLifecycleMethodNames;
    } else {
      lifecycleHooks = options;
    }

    lifecycleHooks.forEach(hookName => {
      const originalHookHandler = cls.prototype[hookName];
      cls.prototype[hookName] = function (...args: any[]): any {
        RtLogger.lifecycle(hookName, displayName, ngType, args);

        if (typeof originalHookHandler === 'function') {
          originalHookHandler.apply(this, args);
        }
      };
    });
  }

  private attachMeasureMethods(cls: any, name: string, methods: string[]): void {
    methods.forEach(methodName => {
      const originalMethod = cls.prototype[methodName];

      cls.prototype[methodName] = function rtMeasureHook(...args: any[]): Function {
        return rtDebugMeasure(originalMethod, this, args, `${name}.${methodName}`);
      };
    });
  }

  private cloneMetadataAnnotations(from: any, to: any): void {
    const keys = Reflect.getMetadataKeys(from);
    keys.forEach(key => {
      Reflect.defineMetadata(key, Reflect.getMetadata(key, from), to);
    });

    if (from.hasOwnProperty('__annotations__')) {
      Object.defineProperty(to, '__annotations__', { value: from.__annotations__ });
    }

    if (from.hasOwnProperty('__paramaters__')) {
      Object.defineProperty(to, '__paramaters__', { value: from.__paramaters__ });
    }

    if (from.hasOwnProperty('__PROP_METADATA__')) {
      Object.defineProperty(to, '__PROP_METADATA__', { value: from.__PROP_METADATA__ });
    }
  }
}
